import { Box, Button, MenuItem, Select, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useLoadMore from "hooks/useLoadMore";
import { useMemo } from "react";
import { USER_TYPE, USER_TYPE_LIST } from "constants/user-type";
import { getUserType } from "util/auth";
import LabelHeader from "components/Label/LabelHeader";
import WinterWheelPriceSettingTable from "features/mst_wheel/components/WinterWheelPriceSettingTable";
import useWinterWheelPriceSetting from "features/mst_wheel/hooks/useWinterWheelPriceSetting";
import WinterWheelPriceSettingModal from "features/mst_wheel/components/WinterWheelPriceSettingModal";
import { PRICE_SETTING_TIRE } from "features/proc_fee/types/tire";
import MenuPriceSetting from "components/menu/MenuPriceSetting";

interface WinterWheelPriceSettingProps {
  role: keyof typeof USER_TYPE;
}

/**
 * WinterWheelPriceSetting Component
 *
 * Provides an interface for managing and configuring winter wheel set selling prices.
 * Allows users to select calculation methods, view and update price lists, and open modals for detailed settings.
 * UI adapts based on user role (e.g., headquarters vs. store).
 * @param {WinterWheelPriceSettingProps} props - The props for the component.
 * @returns {Element} The rendered winter wheel price setting page.
 */
function WinterWheelPriceSetting({ role }: WinterWheelPriceSettingProps) {
  const navigate = useNavigate();
  const isProductRole = useMemo(() => role === USER_TYPE_LIST.headquarters.value, [role]);

  const {
    winterWheelPriceList,
    selectedWinterWheelSelling,
    winterWheelPriceMethod,
    openWinterWheelSellingModal,
    loading,
    updateKey,
    handleSelectWinterWheel,
    handleClearSelectedWinterWheel,
    handleUpdateWinterWheelPrice,
    onLoadMore,
    setWinterWheelPriceMethod,
  } = useWinterWheelPriceSetting({ isProductRole });
  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate("/")} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: { xs: 0, lg: 5 },
          p: { xs: 2, md: 2.5, lg: 5 },
        }}
      >
        <Box
          sx={{
            display: { xs: "flex", xl: "block" },
            justifyContent: "space-between",
            flexBasis: { xs: "100%", xl: "290px" },
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            margin={0}
            textAlign="left"
            fontSize={{ xs: 24, md: 28, lg: 23 }}
            marginBottom="50px"
            color="var(--gray-600)"
            minWidth={360}
            width={{ xs: "100%", lg: "25%" }}
          >
            <span style={{ fontSize: "33px" }}>マスタ管理</span> <br />
            <span style={{ fontSize: "28px" }}>売価設定</span> <br />
          </Typography>
          <MenuPriceSetting />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            width: { xs: "100%", lg: "auto" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: { xs: 3, lg: 9 },
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: { xs: 18, xl: 25 } }}
                variant="h5"
                fontSize="25px"
                color="var(--gray-600)"
                fontWeight="medium"
                marginBottom="12px"
              >
                廉価 ホイールセット売価設定
              </Typography>
              <Typography
                variant="h2"
                fontSize="35px"
                color="var(--black)"
                fontWeight="regular"
                sx={{ fontSize: { xs: 20, md: 24, xl: 35 } }}
              >
                廉価 ホイールセット売価設定を行います
              </Typography>
            </Box>
            {!isProductRole && winterWheelPriceMethod !== undefined && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "end",
                  flexWrap: "wrap",
                  gap: 1,
                  width: { xs: "50%", md: "auto" },
                }}
              >
                <LabelHeader
                  sxBox={{
                    height: { xs: 32, lg: 48 },
                    display: "flex",
                    justifyContent: "center",
                    width: { xs: 290, md: 340, lg: 365 },
                  }}
                  label="廉価 ホイールセット時の売価計算方法"
                />
                <Select
                  sx={{
                    width: { xs: 150, md: 180, lg: 160, xl: 180 },
                    height: { xs: 32, lg: 48 },
                    fontSize: { xs: "16px", md: "18px", xl: "20px" },
                    color: "var(--gray-700)",
                    "& .MuiSelect-select": {
                      padding: "0 16px",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                  value={winterWheelPriceMethod}
                  onChange={(event) => setWinterWheelPriceMethod(Number(event.target.value))}
                >
                  <MenuItem value={PRICE_SETTING_TIRE.SELLING}>売価から計算</MenuItem>
                  <MenuItem value={PRICE_SETTING_TIRE.COST}>原価から計算</MenuItem>
                </Select>
              </Box>
            )}
          </Box>

          {!isProductRole && winterWheelPriceMethod === PRICE_SETTING_TIRE.COST && (
            <Typography
              variant="h2"
              fontSize="35px"
              color="var(--black)"
              fontWeight="regular"
              sx={{ fontSize: { xs: 20, md: 24, xl: 35 } }}
            >
              セット売価計算方法を「売価から計算」を選ぶと下記にリストが表示されます
            </Typography>
          )}

          {!isProductRole && winterWheelPriceMethod === PRICE_SETTING_TIRE.SELLING && (
            <Box
              sx={{
                display: "flex",
                width: { xs: "100%", md: "auto" },
                alignItems: "center",
                justifyContent: "flex-end",
                columnGap: 4,
                marginRight: { xs: 0, lg: 6 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "var(--black)",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  fontWeight: "bold",
                }}
              >
                {getUserType() === "C0002" ? "黒字 本部/企業設定" : "黒字 本部設定"}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "var(--red-800)",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  fontWeight: "bold",
                }}
              >
                赤字 {getUserType() === "C0006" ? "企業上書設定" : "店舗上書設定"}
              </Typography>
            </Box>
          )}

          {((!isProductRole && winterWheelPriceMethod === PRICE_SETTING_TIRE.SELLING) ||
            isProductRole) && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    color: "var(--black)",
                    fontSize: { xs: "14px", lg: "15px" },
                    fontWeight: "bold",
                    marginLeft: "auto",
                    marginRight: { xs: 0, lg: 5 },
                  }}
                >
                  下記の設定価格より4本特価設定が優先されます。
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "var(--black)",
                    fontSize: { xs: "14px", lg: "15px" },
                    fontWeight: "bold",
                    marginLeft: "auto",
                    marginRight: { xs: 0, lg: 5 },
                  }}
                >
                  4本特価の設定がない冬ホイールは、下記の売価が設定になります。
                </Typography>
                <Box sx={{ overflowX: "auto", width: { xs: "100%", lg: "auto" }, mt: 1.5 }}>
                  <WinterWheelPriceSettingTable
                    key={updateKey}
                    winterWheelPriceList={winterWheelPriceList}
                    onUpdate={handleUpdateWinterWheelPrice}
                    openModal={(item) => handleSelectWinterWheel(item)}
                  />
                </Box>
              </>
            )}
          {renderNodeLoader()}
        </Box>
      </Box>

      {openWinterWheelSellingModal && (
        <WinterWheelPriceSettingModal
          showWinterWheelModal={openWinterWheelSellingModal}
          setShowWinterWheelModal={handleClearSelectedWinterWheel}
          selectedWinterWheelSelling={selectedWinterWheelSelling}
        />
      )}
    </>
  );
}

export default WinterWheelPriceSetting;
